import { Button } from "@/components/ui/button";
import { Icons } from "@/components/ui/icons";
import { Progress } from "@/components/ui/progress";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import { cn } from "@/lib/utils";
import { Check, File, Upload, X } from "lucide-react";
import { useState } from "react";

interface MediaUploadProps {
  onUpload: (cloudStorageObjectId: string, file: File) => void;
  onRemove?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
  disabled?: boolean;
  currentFile?: File | null;
  currentUrl?: string | null;
  title?: string;
  description?: string;
}

export function MediaUpload({
  onUpload,
  onRemove,
  accept = "*/*",
  maxSize = 50 * 1024 * 1024, // 50MB default
  className,
  disabled,
  currentFile,
  currentUrl,
  title,
  description,
}: MediaUploadProps) {
  const [file, setFile] = useState<File | null>(currentFile || null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentUrl || null,
  );
  const [error, setError] = useState<string | null>(null);
  const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null);

  const {
    uploadFile: uploadToStorage,
    isUploading,
    uploadProgress,
  } = useMediaUpload({
    onSuccess: (media) => {
      setUploadedMediaId(media.cloudStorageObjectId);
      if (file) {
        onUpload(media.cloudStorageObjectId, file);
      }
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file size
    if (selectedFile.size > maxSize) {
      setError(`File size must be less than ${maxSize / 1024 / 1024}MB`);
      return;
    }

    setError(null);
    setFile(selectedFile);

    // Create preview URL for images
    if (selectedFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }

    // Start upload immediately
    uploadToStorage(selectedFile, {
      title: title || selectedFile.name,
      description: description || `Uploaded file: ${selectedFile.name}`,
    });
  };

  const handleRemove = () => {
    setFile(null);
    setPreviewUrl(null);
    setUploadedMediaId(null);
    setError(null);
    if (onRemove) {
      onRemove();
    }
  };

  const isImage = file?.type.startsWith("image/");

  return (
    <div className={cn("space-y-4", className)}>
      {!file && !isUploading && (
        <label
          className={cn(
            "flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100",
            disabled && "opacity-50 cursor-not-allowed",
            error && "border-red-500",
          )}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Upload className="w-10 h-10 mb-3 text-gray-400" />
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500">
              {accept === "*/*" ? "Any file type" : accept}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Max size: {maxSize / 1024 / 1024}MB
            </p>
          </div>
          <input
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept={accept}
            disabled={disabled || isUploading}
          />
        </label>
      )}

      {(file || isUploading) && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isImage && previewUrl ? (
                <img
                  src={previewUrl}
                  alt={file?.name}
                  className="w-16 h-16 object-cover rounded"
                />
              ) : (
                <File className="w-10 h-10 text-gray-400" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file?.name}
                </p>
                <p className="text-sm text-gray-500">
                  {file ? `${(file.size / 1024 / 1024).toFixed(2)}MB` : ""}
                </p>
              </div>
            </div>
            {!isUploading && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {uploadedMediaId && !isUploading && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <Check className="h-4 w-4" />
              <span>Upload complete</span>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
